import React, { useCallback } from "react";
import { ReactComponent as ProjectsIcon } from "icons/projects-icon.svg";
import { FiPackage } from "react-icons/fi";
import { useNavigate, useLocation } from "@reach/router";
import { useRfxProjectTypes } from "newui/features/Rfx/hooks/useRfxProjectTypes";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { useCurrentCompany } from "hooks/auth";
import { AppMenuItem } from "./AppMenuItem/AppMenuItem";
import { ExpandableMenuItem, ExpandableMenuChildItem } from "./ExpandableMenuItem";

interface RfxProjectsMenuProps {
	isCollapsed: boolean;
	onNavigate?: () => void;
}

const STORAGE_KEY = "rfxProjectsMenu_expanded";

export const RfxProjectsMenu: React.FC<RfxProjectsMenuProps> = ({
	isCollapsed,
	onNavigate,
}) => {
	const currentCompany = useCurrentCompany();
	const defaultType = currentCompany?.default_project_type ?? null;
	const typeCount = currentCompany?.project_type_count ?? 0;
	const { clientPaths } = useNavigationPaths();
	const navigate = useNavigate();
	const location = useLocation();
	const currentTypeSlug = new URLSearchParams(location.search).get("type");

	// Only fetch full list when there are multiple types
	const { data: projectTypes, isLoading: isLoadingTypes } = useRfxProjectTypes(typeCount > 1);

	const handleChildClick = useCallback(
		(link: string) => (e: React.MouseEvent) => {
			e.preventDefault();
			navigate(link);
			// @reach/router's navigate uses pushState which doesn't fire popstate.
			// nuqs's useQueryState listens for popstate to detect query param changes.
			window.dispatchEvent(new PopStateEvent("popstate"));
			if (onNavigate) {
				onNavigate();
			}
		},
		[navigate, onNavigate],
	);

	if (!clientPaths) {
		return null;
	}

	// Single type: use the default from company data (no extra API call)
	if (typeCount <= 1) {
		return (
			<AppMenuItem
				name={defaultType?.name ?? "Projects and Assets"}
				icon={<ProjectsIcon />}
				link={clientPaths.rfxProjects.my}
				total={defaultType?.projectCount ?? 0}
				isCollapsed={isCollapsed}
				testId="projects-and-assets"
				tourName="ProjectsAndAssets"
				onNavigate={onNavigate}
			/>
		);
	}

	// Multiple types: show expandable menu
	const activeTypes = projectTypes?.filter((t) => t.isActive) ?? [];
	const totalProjects = activeTypes.length > 0
		? activeTypes.reduce((sum, t) => sum + t.projectCount, 0)
		: defaultType?.projectCount ?? 0;

	return (
		<ExpandableMenuItem
			icon={<ProjectsIcon />}
			label="Portfolio"
			storageKey={STORAGE_KEY}
			isCollapsed={isCollapsed}
			collapsedLink={clientPaths.rfxProjects.my}
			testId="projects-menu"
			tourName="ProjectsAndAssets"
			badge={totalProjects}
			onNavigate={onNavigate}
		>
			{isLoadingTypes ? (
				<div className="py-2 px-4">
					<span className="font-roboto text-sm text-gray-400">Loading...</span>
				</div>
			) : (
				activeTypes.map((type) => (
					<ExpandableMenuChildItem
						key={type.id}
						name={type.name}
						link={clientPaths.rfxProjectsWithType(type.slug)}
						isActive={currentTypeSlug === type.slug}
						icon={<FiPackage size={14} />}
						count={type.projectCount}
						testId={`project-type-${type.slug}`}
						onClick={handleChildClick(clientPaths.rfxProjectsWithType(type.slug))}
					/>
				))
			)}
		</ExpandableMenuItem>
	);
};
